
import React, { useEffect } from 'react';
import { useAllProfiles } from '@/hooks/profile/use-all-profiles';
import { useAuth } from '@/contexts/AuthContext';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { AccessDenied } from './panels/AccessDenied';
import { UserManagementHeader } from './panels/UserManagementHeader';
import { UserFilterPanel } from './panels/UserFilterPanel';
import { UserTable } from './panels/UserTable';
import { UserDialogs } from './panels/UserDialogs';
import { Button } from '@/components/ui/button';

export const UserManagementPanel: React.FC = () => {
  // Force re-fetch data to ensure we get the latest profiles
  const { data: profiles, isLoading, refetch, error, isError, isRefetching } = useAllProfiles();
  const { userRole, user } = useAuth();
  const [inviteDialogOpen, setInviteDialogOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [editingUser, setEditingUser] = React.useState<{
    id: string;
    firstName: string;
    lastName: string;
  } | null>(null);
  const [userToDelete, setUserToDelete] = React.useState<{
    id: string;
    email: string;
    name: string;
  } | null>(null);
  const [userToResetPassword, setUserToResetPassword] = React.useState<{
    id: string;
    email: string;
    name: string;
  } | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = React.useState<string | null>(null);
  const [errorDetails, setErrorDetails] = React.useState<string | null>(null);

  const isAdmin = userRole === 'admin';
  
  // Check for current user missing from profiles
  const currentUserInProfiles = user && profiles?.some(profile => profile.id === user.id);

  // Filter profiles based on search query
  const filteredProfiles = profiles?.filter(profile => {
    const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.toLowerCase();
    const email = profile.email || ''; 
    const status = profile.status || 'active';
    
    return fullName.includes(searchQuery.toLowerCase()) 
      || profile.role.includes(searchQuery.toLowerCase())
      || email.toLowerCase().includes(searchQuery.toLowerCase())
      || status.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Force refetch on initial load only (removed auto-refresh interval)
  useEffect(() => {
    console.log("UserManagementPanel mounted, forcing refresh");
    refetch();
  }, [refetch]);
  
  // Also refetch when invite dialog closes (new user might have been added)
  useEffect(() => {
    if (!inviteDialogOpen) {
      console.log("Invite dialog closed, refreshing user list");
      refetch();
    }
  }, [inviteDialogOpen, refetch]);

  // Extract detailed error information
  useEffect(() => {
    if (error instanceof Error) {
      setErrorDetails(error.message);
    } else if (error) {
      setErrorDetails(JSON.stringify(error));
    } else {
      setErrorDetails(null);
    }
  }, [error]);

  // Check if user has admin access
  if (!isAdmin) {
    return <AccessDenied />;
  }

  // Show error state if there was an issue loading profiles
  if (isError && error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4 text-center">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <h3 className="text-lg font-semibold">Failed to load users</h3>
        <p className="text-muted-foreground">
          {errorDetails || 'An unknown error occurred'}
        </p>
        <div className="bg-muted p-4 rounded-md max-w-lg overflow-auto text-xs text-left">
          <pre>{errorDetails}</pre>
        </div>
        <Button 
          onClick={() => {
            toast.success("Attempting to refresh user list");
            refetch();
          }} 
          variant="outline"
          disabled={isRefetching}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
          {isRefetching ? 'Refreshing...' : 'Try Again'}
        </Button>
      </div>
    );
  }

  return (
    <>
      <UserManagementHeader 
        onRefresh={() => {
          refetch();
          toast.success("Refreshing user list");
        }} 
        onOpenInviteDialog={() => setInviteDialogOpen(true)} 
      />

      <UserFilterPanel 
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      <UserTable 
        profiles={filteredProfiles}
        isLoading={isLoading || isRefetching}
        currentUser={user}
        isUpdatingStatus={isUpdatingStatus}
        onStatusUpdate={setIsUpdatingStatus}
        onRefresh={refetch}
        onEditUser={setEditingUser}
        onDeleteUser={setUserToDelete}
        onResetPassword={setUserToResetPassword}
      />

      <UserDialogs
        inviteDialogOpen={inviteDialogOpen}
        editingUser={editingUser}
        userToDelete={userToDelete}
        userToResetPassword={userToResetPassword}
        onInviteDialogClose={() => setInviteDialogOpen(false)}
        onEditDialogClose={() => setEditingUser(null)}
        onDeleteDialogClose={() => setUserToDelete(null)}
        onResetPasswordDialogClose={() => setUserToResetPassword(null)}
        onRefresh={refetch}
      />
    </>
  );
};
